// Agency context: who I am in this agency, the team, and a small notice area. Loaded once
// per agency and shared by every page under /agencies/:agencyId.
import { createContext, useCallback, useContext, useMemo, useState, useEffect, useRef, type ReactNode } from 'react';
import { NavLink, Outlet, useNavigate, useParams, useSearchParams } from 'react-router';
import { Banner, Loading, useQuery } from '@acc/web-shared';
import { isManager, ROLE_LABELS, type AgencyRole } from '@acc/domain';

export interface TeamMember {
  membershipId: string;
  displayName: string | null;
  email: string;
  role: AgencyRole;
  status: 'Invited' | 'Active' | 'Disabled';
  seesAllQuotes: boolean;
}

interface AgencyCtx {
  agencyId: string;
  agencyName: string;
  me: { membershipId: string; role: AgencyRole; seesAllQuotes: boolean };
  manager: boolean;
  team: TeamMember[];
  base: string;                       // /agencies/:agencyId
  path: (p: string) => string;        // API path under the agency
  name: (membershipId: string | null | undefined) => string;
  reloadTeam: () => void;
  notify: (tone: 'ok' | 'warn' | 'bad', text: string) => void;
}

const Ctx = createContext<AgencyCtx | null>(null);
export const useAgency = () => {
  const c = useContext(Ctx);
  if (!c) throw new Error('useAgency outside AgencyLayout');
  return c;
};

export function AgencyLayout() {
  const { agencyId = '' } = useParams();
  const agency = useQuery<{ agency: { agencyId: string; name: string } }>(`/v1/agencies/${agencyId}`);
  const team = useQuery<{ members: TeamMember[]; me: AgencyCtx['me'] }>(`/v1/agencies/${agencyId}/team`);
  const dash = useQuery<{ unassignedIntake: number; followUps: number; pendingApprovals: number | null; callsNeedingFollowUp: number }>(`/v1/agencies/${agencyId}/dashboard`);
  const [notice, setNotice] = useState<{ tone: 'ok' | 'warn' | 'bad'; text: string } | null>(null);
  const noticeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const notify = useCallback((tone: 'ok' | 'warn' | 'bad', text: string) => {
    if (noticeTimer.current !== null) clearTimeout(noticeTimer.current);
    setNotice({ tone, text });
    if (tone === 'ok') {
      noticeTimer.current = setTimeout(() => {
        setNotice(null);
        noticeTimer.current = null;
      }, 5000);
    }
    window.scrollTo({ top: 0 });
  }, []);
  useEffect(() => () => {
    if (noticeTimer.current !== null) clearTimeout(noticeTimer.current);
  }, []);

  const value = useMemo<AgencyCtx | null>(() => {
    if (!agency.data || !team.data) return null;
    const members = team.data.members;
    return {
      agencyId, agencyName: agency.data.agency.name, me: team.data.me, manager: isManager(team.data.me.role), team: members,
      base: `/agencies/${agencyId}`, path: (p) => `/v1/agencies/${agencyId}${p}`,
      name: (id) => { if (!id) return 'Unassigned'; const m = members.find((x) => x.membershipId === id); return m ? m.displayName ?? m.email : 'Former team member'; },
      reloadTeam: team.reload,
      notify,
    };
  }, [agency.data, team.data, agencyId, team.reload, notify]);

  if (agency.error || team.error) return <main className="page"><Banner tone="bad">{(agency.error ?? team.error)!.message === 'not found' ? 'This agency is not available to your account.' : (agency.error ?? team.error)!.message}</Banner></main>;
  if (!value) return <Loading label="Opening your agency…" />;
  const counts = dash.data;
  const item = (to: string, label: string, icon: string, count?: number | null) => (
    <NavLink to={`${value.base}${to}`} end={to === ''} className={({ isActive }) => (isActive ? 'active' : '')}>
      <span className="nav-label"><span className="nav-icon" aria-hidden="true">{icon}</span><span>{label}</span></span>{count ? <span className="count">{count}</span> : null}
    </NavLink>
  );
  return (
    <Ctx.Provider value={value}>
      <div className="shell">
        <nav className="sidenav" aria-label="Agency">
          <div className="agency"><strong>Agency Command Center</strong><span>{value.agencyName} · {ROLE_LABELS[value.me.role]}</span></div>
          {item('', 'Dashboard', '⌂')}
          {item('/calls', 'Phone Calls', '☎', counts?.callsNeedingFollowUp)}
          {item('/quotes', 'Quote Management', '▤')}
          {item('/follow-ups', 'Requotes / Follow-up', '↻', counts?.followUps)}
          {item('/sales', 'Completed Sales', '▣')}
          {item('/intake', 'Quote Intake', '✉', counts?.unassignedIntake)}
          {value.manager && item('/approvals', 'Missed Approvals', '✓', counts?.pendingApprovals)}
          {item('/reports', 'Reports', '▥')}
          {item('/settings', 'Settings', '⚙')}
        </nav>
        <main className="page">
          <SearchBox base={value.base} />
          {notice && <div onClick={() => setNotice(null)}><Banner tone={notice.tone}>{notice.text}</Banner></div>}
          <Outlet context={{ refreshCounts: dash.reload, clearNotice: () => setNotice(null) }} />
        </main>
      </div>
    </Ctx.Provider>
  );
}

// The prototype's top-bar search: submits to the results page; keeps the term while there.
function SearchBox({ base }: { base: string }) {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [term, setTerm] = useState(params.get('q') ?? '');
  return (
    <form role="search" className="searchbar" onSubmit={(e) => { e.preventDefault(); const q = term.trim(); if (q.length >= 2) nav(`${base}/search?q=${encodeURIComponent(q)}`); }}>
      <input type="search" value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Search quotes, clients or calls..." aria-label="Search quotes, clients or calls" minLength={2} maxLength={100} />
    </form>
  );
}

export function PageHead({ title, sub, children }: { title: string; sub?: ReactNode; children?: ReactNode }) {
  return (
    <div className="page-head">
      <div><h1>{title}</h1>{sub && <p className="sub">{sub}</p>}</div>
      {children && <div className="toolbar" style={{ margin: 0 }}>{children}</div>}
    </div>
  );
}

export const shortDate = (iso: string | null | undefined) => (iso ? new Date(iso.length === 10 ? `${iso}T12:00:00` : iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—');
export const dateTime = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '—');
